import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FRQTextNote from 'chameleon/packages/blue-book-player/ToolbarManager/FRQTextNote';
import {
  HYBRID_COURSES_SUBJECTS,
  AP_COMPUTER_SCIENCE_A,
} from 'chameleon/packages/blue-book-player/constants/subjects';
import { getItemByRef } from 'chameleon/packages/blue-book-player/utils';
import { KEYS } from 'chameleon/packages/blue-book-player/constants/keyCodes';
import Toolbar from './Toolbar';
import { BluebookContext } from '../state/BluebookContext';
import {
  BLACKLIST,
  ToolbarSeparator,
  WHITELIST,
  getOrderedAndGroupedFormattingOptions,
} from './constants';
import useGetPlayerEl from '../hooks/useGetPlayerEl';
import useListenToRenderedQuestions from '../hooks/useListenToRenderedQuestions';
import localClipboard from './utils';

const FILE_UPLOAD_SELECTOR = '.lrn-fileupload-toolbar-outlet';

const getConfigFromFormattingOptions = (formattingOptions = []) => {
  const filteredList = formattingOptions.filter(
    (formattingOption) =>
      !BLACKLIST.includes(formattingOption) &&
      formattingOption !== ToolbarSeparator,
  );

  const adaptedList = [...filteredList, ...WHITELIST];

  return getOrderedAndGroupedFormattingOptions(adaptedList);
};

const ToolbarManager = ({ learnosityApp, subject, userData, isSecure }) => {
  const { state: playerState } = useContext(BluebookContext);
  const [toolbars, setToolbars] = useState([]);
  const playerEl = useGetPlayerEl();
  const handledQuestions = useRef([]);

  useListenToRenderedQuestions(
    playerState,
    learnosityApp,
    (learnosityReference, questionApi) => {
      const questionId = questionApi.getQuestion().response_id;

      const formattingOptions = getConfigFromFormattingOptions(
        questionApi.getQuestion().formatting_options,
      );
      // keep track of already handled questions to avoid adding handlers or rendering toolbars twice
      if (handledQuestions.current.includes(questionId)) {
        return;
      }
      handledQuestions.current.push(questionId);

      if (toolbars[questionId.substring(0, questionId.length - 2)]) {
        return;
      } else {
        const questionRef = playerEl.querySelectorAll(
          `[id="${questionId}"] .lrn_texteditor`,
        );

        if (String(subject.id) === String(AP_COMPUTER_SCIENCE_A)) {
          questionRef.forEach((ref) => {
            const editor = ref.querySelector('.lrn_texteditor_editable');

            //we need to prevent the user from removing the code editor
            const handleKeyDown = (e) => {
              if (
                e.key === KEYS.BACKSPACE &&
                !editor.innerText.replace(/[\u200B]/g, '').trim()
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            };

            const handleClick = () => {
              ref
                .querySelector('.lrn-editor-toolbar-button-codeEditor')
                .click();
            };

            editor.addEventListener('click', handleClick);
            editor.addEventListener('keydown', handleKeyDown, true);
          });
          return;
        }

        const itemRef = getItemByRef(playerEl, learnosityReference);

        const isFirst =
          itemRef.querySelector(`.lrn_texteditor`) === questionRef[0];

        const newToolbars = {};
        questionRef.forEach((ref, index) => {
          if (!ref.querySelector(FILE_UPLOAD_SELECTOR)) {
            const preElement = document.createElement('div');
            ref.prepend(preElement);
            newToolbars[`${questionId}-${index}`] = {
              preElement,
              formattingOptions,
              isFirst,
            };
          }
        });

        if (questionRef) {
          setToolbars((prevToolbars) => ({
            ...prevToolbars,
            ...newToolbars,
          }));
        }
      }
    },
  );

  useEffect(() => {
    document.addEventListener('copy', () => {
      const copiedText = window.getSelection().toString();
      localClipboard.writeText(copiedText);
    });
    document.addEventListener(
      'cut',
      () => {
        const cutText = window.getSelection().toString();
        localClipboard.writeText(cutText);
      },
      true,
    );
  }, []);

  return (
    <>
      {Object.keys(toolbars).map((toolbarKey) =>
        createPortal(
          <>
            {HYBRID_COURSES_SUBJECTS.includes(parseInt(subject.id, 10)) &&
              toolbars[toolbarKey].isFirst && <FRQTextNote />}
            <Toolbar
              key={toolbarKey}
              element={toolbars[toolbarKey].preElement.parentElement}
              formattingOptions={toolbars[toolbarKey].formattingOptions}
              isBluebookDigitalExam={subject.useBluebookDigitalExam}
              subjectId={subject.id}
              userData={userData}
              isSecure={isSecure}
            />
          </>,
          toolbars[toolbarKey].preElement,
        ),
      )}
    </>
  );
};

export default ToolbarManager;
